import { useCallback } from 'react';
import { ACTIONS } from 'src/constants/common';
import { COUNTRY_NAME_BY_CURRENCY_MAP } from 'src/constants/country-name-by-currency-map';
import { INSTALLMENTS_COUNT_LIST_FOR_DEFAULT_PROMO } from 'src/constants/learn-more';
import {
  INSTALLMENTS_COUNT_DEFAULT,
  useAppContext,
} from 'src/context/store/app-provider';
import { useDocumentClick } from 'src/hooks/use-document-click';
import { type InfoAttrName } from 'src/types/info-attr-name';
import { SegmentEventType } from 'src/types/segment';
import { type ThemeType } from 'src/types/theme-type';
import { checkInstallmentsCount } from 'src/utils/check-installments-count';
import { getDataFromDataset } from 'src/utils/get-data-from-dataset';

export const usePromoSetup = (
  isAlshaya: boolean,
  logAnalyticEvent: (event) => void,
  infoAttrName: InfoAttrName
) => {
  const { currency, installmentsCount, publicKey, dispatch } =
    useAppContext<ThemeType>();

  const learnMoreClicked = useCallback(() => {
    const snippetType = Array<number | undefined>(3, 4).includes(
      installmentsCount
    )
      ? 'fullInformation'
      : 'notFullInformation';

    logAnalyticEvent({
      event: SegmentEventType.LEARN_MORE_CLICKED,
      publicKey: publicKey ? 'yes' : 'no',
      merchantIntegrationType: 'popupOnly',
      planSelected: installmentsCount,
      snippetType: isAlshaya ? 'alshaya' : snippetType,
      merchantCountry: COUNTRY_NAME_BY_CURRENCY_MAP[currency],
    });
  }, [currency, installmentsCount, isAlshaya, logAnalyticEvent, publicKey]);

  const openPopup = useCallback(
    (dataSet: DOMStringMap) => {
      const { tabbyInstallmentsCount: tabbyInstallmentsCountRaw } =
        getDataFromDataset(dataSet);

      const tabbyInstallmentsCount = checkInstallmentsCount(
        INSTALLMENTS_COUNT_DEFAULT
      )(tabbyInstallmentsCountRaw);

      const infoAttr = dataSet[infoAttrName];
      const [dataProductType] = infoAttr ? infoAttr.split(':') : ['', ''];

      switch (dataProductType) {
        case 'installments':
          switch (true) {
            case INSTALLMENTS_COUNT_LIST_FOR_DEFAULT_PROMO.includes(
              tabbyInstallmentsCount
            ):
              dispatch({
                type: ACTIONS.SHOW_LEARN_MORE,
                payload: { pix: true },
              });
              break;
            default:
              dispatch({
                type: ACTIONS.SHOW_LEARN_MORE,
                payload: { default: true },
              });
          }

          learnMoreClicked();

          break;

        default:
        // no default case
      }
    },
    [infoAttrName, learnMoreClicked, dispatch]
  );

  const documentClickCallback = useCallback(
    (e) => {
      const element = e.target as HTMLElement;
      const { dataset } = element;

      if (!dataset[infoAttrName]) return;

      e.preventDefault();

      dispatch({
        type: ACTIONS.UPDATE_FROM_DATASET,
        payload: getDataFromDataset(dataset),
      });
      openPopup(dataset);
    },
    [dispatch, infoAttrName, openPopup]
  );

  useDocumentClick('documentClick', documentClickCallback);
};
